import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@cargomatic/ui';
import { X } from 'lucide-react';
import { sluggify } from 'utils/localization/utils';
import { getCarrierScacs } from 'utils/OrderWebAPIUtils';
import { uniqBy } from 'lodash';
import { equipmentTypes, equipmentSizes } from 'constants/Equipment';
import { humanize } from 'underscore.string';
import { useTranslation } from 'react-i18next';
import { DataTableFilter } from '../filters/DataTableFilter';
import { DataTableDateFilter } from '../filters/DataTableDateFilter';
import { DataTableCustomerSearchFilter } from '../filters/DataTableCustomerSearchFilter';
import { customerTypes, hazardous, orderTypes, statusList, } from '../filters/filters';
import { DataTableTextFilter } from '../filters/DataTableTextFilter';
import { RATE_CONDITION_COLUMN_PREFIX } from 'constants/LaneRates';
import { FiltersDropdown } from '../filters/FiltersDropdown';
import { cn } from 'utils/ui/classes';
import Tooltip from 'components/ui/Tooltip';
const laneFilters = [
    'customer',
    'customerType',
    'origin',
    'destination',
    'status',
    'effectiveStart',
    'effectiveEnd',
];
const laneRateFiltersSet = new Set(laneFilters);
const rateConditionFilters = [
    `${RATE_CONDITION_COLUMN_PREFIX}Customer`,
    `${RATE_CONDITION_COLUMN_PREFIX}OrderType`,
    `${RATE_CONDITION_COLUMN_PREFIX}EquipmentType`,
    `${RATE_CONDITION_COLUMN_PREFIX}EquipmentSize`,
    `${RATE_CONDITION_COLUMN_PREFIX}Hazardous`,
    `${RATE_CONDITION_COLUMN_PREFIX}SteamshipOrRailroad`,
];
const rateConditionFiltersSet = new Set(rateConditionFilters);
export function DataTableToolbar({ table, children }) {
    var _a;
    const [items, setItems] = useState([]);
    const [activeFilters, setActiveFilters] = useState(laneFilters);
    const activeFiltersSet = useMemo(() => new Set(activeFilters), [activeFilters]);
    const columnFiltersState = (_a = table === null || table === void 0 ? void 0 : table.getState()) === null || _a === void 0 ? void 0 : _a.columnFilters;
    useEffect(() => {
        const filteredColumnIds = (columnFiltersState !== null && columnFiltersState !== void 0 ? columnFiltersState : []).map((col) => col.id);
        const filteredColumnIdsSet = new Set(filteredColumnIds);
        setActiveFilters(Array.from(activeFiltersSet.union(filteredColumnIdsSet)));
    }, [columnFiltersState]);
    const hasLaneRateFilters = useMemo(() => {
        return activeFiltersSet.intersection(laneRateFiltersSet).size > 0;
    }, [activeFilters]);
    const hasRateConditionFilters = useMemo(() => {
        return activeFiltersSet.intersection(rateConditionFiltersSet).size > 0;
    }, [activeFilters]);
    const { t } = useTranslation();
    function handleReset() {
        setActiveFilters(laneFilters);
        table.resetColumnFilters();
    }
    function handleRemoveFilter(filter) {
        table.getColumn(filter).setFilterValue(undefined);
    }
    async function fetchData() {
        let { entity: { OCEAN_SCAC_CODES: oceanScacs }, } = await getCarrierScacs();
        setItems(uniqBy(oceanScacs, (item) => item.ocean_scac).map((item) => ({
            label: item.carrier,
            value: item.ocean_scac,
        })));
    }
    useEffect(() => {
        fetchData();
    }, []);
    const mappedEquipmentTypes = Object.keys(equipmentTypes).map((equipmentType) => ({
        label: t(sluggify(humanize(equipmentType))),
        value: equipmentType,
    }));
    return (React.createElement("div", { className: "flex justify-between flex-wrap items-start gap-4" },
        React.createElement("div", { className: "flex-1 gap-2" },
            hasLaneRateFilters && (React.createElement("div", { className: "" },
                React.createElement("p", { className: "text-sm leading-none mb-2 font-medium" }, "Lane Filters"),
                React.createElement("div", { className: "flex gap-2 flex-wrap" },
                    activeFilters.includes('customer') &&
                        table.getColumn('customerName') && (React.createElement(DataTableCustomerSearchFilter, { column: table.getColumn('customerId'), title: "Customer" })),
                    activeFilters.includes('customerType') &&
                        table.getColumn('customerType') && (React.createElement(DataTableFilter, { column: table.getColumn('customerType'), title: "Customer Type", options: customerTypes })),
                    activeFilters.includes('origin') &&
                        table.getColumn('origin') && (React.createElement(DataTableTextFilter, { column: table.getColumn('origin'), title: "Origin" })),
                    activeFilters.includes('destination') &&
                        table.getColumn('destination') && (React.createElement(DataTableTextFilter, { column: table.getColumn('destination'), title: "Destination" })),
                    activeFilters.includes('status') &&
                        table.getColumn('status') && (React.createElement(DataTableFilter, { column: table.getColumn('status'), title: "Status", options: statusList })),
                    activeFilters.includes('effectiveStart') &&
                        table.getColumn('effectiveStart') && (React.createElement(DataTableDateFilter, { column: table.getColumn('effectiveStart'), title: "Validity Start" })),
                    activeFilters.includes('effectiveEnd') &&
                        table.getColumn('effectiveEnd') && (React.createElement(DataTableDateFilter, { column: table.getColumn('effectiveEnd'), title: "Validity End" }))))),
            hasRateConditionFilters && (React.createElement("div", { className: "mt-4" },
                React.createElement("p", { className: "text-sm font-medium leading-none" }, "Rate Condition Filters"),
                React.createElement("div", { className: "flex gap-2 mt-2 items-center flex-wrap" },
                    activeFilters.includes(`${RATE_CONDITION_COLUMN_PREFIX}Customer`) &&
                        table.getColumn(`${RATE_CONDITION_COLUMN_PREFIX}Customer`) && (React.createElement(DataTableCustomerSearchFilter, { column: table.getColumn(`${RATE_CONDITION_COLUMN_PREFIX}Customer`), title: "Customer" })),
                    activeFilters.includes(`${RATE_CONDITION_COLUMN_PREFIX}OrderType`) &&
                        table.getColumn(`${RATE_CONDITION_COLUMN_PREFIX}OrderType`) && (React.createElement(DataTableFilter, { column: table.getColumn(`${RATE_CONDITION_COLUMN_PREFIX}OrderType`), title: "Order Type", options: orderTypes })),
                    activeFilters.includes(`${RATE_CONDITION_COLUMN_PREFIX}EquipmentType`) &&
                        table.getColumn(`${RATE_CONDITION_COLUMN_PREFIX}EquipmentType`) && (React.createElement(DataTableFilter, { column: table.getColumn(`${RATE_CONDITION_COLUMN_PREFIX}EquipmentType`), title: "Equip.", options: mappedEquipmentTypes })),
                    activeFilters.includes(`${RATE_CONDITION_COLUMN_PREFIX}EquipmentSize`) &&
                        table.getColumn(`${RATE_CONDITION_COLUMN_PREFIX}EquipmentSize`) && (React.createElement(DataTableFilter, { column: table.getColumn(`${RATE_CONDITION_COLUMN_PREFIX}EquipmentSize`), title: "Equipment Size", options: equipmentSizes.map((size) => ({
                            label: size,
                            value: size,
                        })) })),
                    activeFilters.includes(`${RATE_CONDITION_COLUMN_PREFIX}Hazardous`) &&
                        table.getColumn(`${RATE_CONDITION_COLUMN_PREFIX}Hazardous`) && (React.createElement(DataTableFilter, { column: table.getColumn(`${RATE_CONDITION_COLUMN_PREFIX}Hazardous`), title: "Hazardous", options: hazardous })),
                    activeFilters.includes(`${RATE_CONDITION_COLUMN_PREFIX}SteamshipOrRailroad`) &&
                        table.getColumn(`${RATE_CONDITION_COLUMN_PREFIX}SteamshipOrRailroad`) && (React.createElement(DataTableFilter, { column: table.getColumn(`${RATE_CONDITION_COLUMN_PREFIX}SteamshipOrRailroad`), title: "Steamship/Railroad", className: "max-h-96", options: items })))))),
        React.createElement("div", { className: cn('flex gap-2 flex-wrap', {
                'mt-5': hasLaneRateFilters || hasRateConditionFilters,
            }) },
            React.createElement(Tooltip, { content: "Reset Filters" },
                React.createElement(Button, { variant: "outline", size: "icon", onClick: handleReset, className: "" },
                    React.createElement(X, { className: "w-4 h-4" }))),
            React.createElement(FiltersDropdown, { activeFilters: activeFilters, onCheckedChange: setActiveFilters, onReset: handleReset, onRemoveFilter: handleRemoveFilter }),
            children)));
}
